import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../lib/axios';
import { useToast } from '../hooks/use-toast';
import { Loader2, Printer, ChevronLeft } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function ReceiptPage() {
  const { transactionId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [receipt, setReceipt] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchReceipt = async () => {
      try {
        const res = await api.get(`/billing/receipt/${transactionId}`);
        if (res.data.success) {
          setReceipt(res.data.data);
        }
      } catch (error) {
        toast({ 
          title: "Error", 
          description: "Could not load invoice.", 
          variant: "destructive" 
        });
      } finally {
        setLoading(false);
      }
    };
    fetchReceipt();
  }, [transactionId, toast]);

  const handlePrint = () => window.print();

  if (loading) return (
    <div className="h-screen flex items-center justify-center bg-slate-50">
      <Loader2 className="w-8 h-8 animate-spin text-gray-600" />
    </div>
  );

  if (!receipt) return (
    <div className="p-20 text-center text-gray-500">Document Not Found.</div>
  );

  // Safely extract data from the new API structure
  const invoiceDate = new Date(receipt.created_at).toLocaleDateString('en-GB', { 
    day: '2-digit', month: 'short', year: 'numeric' 
  });
  
  const { business, customer, lineItems, summary } = receipt;

  return (
    <div className="min-h-screen bg-gray-100 py-8 px-4 font-sans text-sm text-gray-900">
      
      {/* Print Media Query isolated to #printable-invoice */}
      <style dangerouslySetInnerHTML={{ __html: `
        @media print {
          @page { size: A4; margin: 0; }
          body { background-color: white !important; -webkit-print-color-adjust: exact; }
          body * { visibility: hidden; }
          #printable-invoice, #printable-invoice * { visibility: visible; }
          #printable-invoice {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            margin: 0 !important;
            padding: 15mm !important;
            border: none !important;
            box-shadow: none !important;
          }
        }
      `}} />

      {/* Action Bar */}
      <div className="max-w-4xl mx-auto mb-6 flex justify-between items-center print-hidden">
        <Button variant="outline" onClick={() => navigate(-1)} className="bg-white">
          <ChevronLeft className="w-4 h-4 mr-2" /> Back
        </Button>
        <Button onClick={handlePrint} className="bg-gray-800 text-white hover:bg-gray-700">
          <Printer className="w-4 h-4 mr-2" /> Print Invoice
        </Button>
      </div>

      {/* A4 Invoice Container */}
      <div id="printable-invoice" className="max-w-4xl mx-auto bg-white border border-gray-300 shadow-sm p-10">
        
        {/* Header Section */}
        <div className="flex justify-between items-start mb-8">
          <div>
            {business?.logoUrl ? (
              <img 
                src={business.logoUrl} 
                alt="Logo" 
                className="h-12 mb-4 object-contain"
                onError={(e) => e.target.style.display = 'none'} // Hide broken images cleanly
              />
            ) : null}
            <h2 className="text-xl font-bold mb-1">{business?.name || 'Your Company Name'}</h2>
            <div className="text-gray-600 leading-tight whitespace-pre-wrap">
              {business?.address}
              <br />Email: {business?.email}
              {business?.website && <><br />Web: {business.website}</>}
            </div>
          </div>
          
          <div className="text-right">
            <h1 className="text-2xl font-semibold text-gray-800 tracking-wide mb-4">TAX INVOICE</h1>
            <table className="ml-auto text-left text-sm">
              <tbody>
                <tr>
                  <td className="pr-4 text-gray-500 font-medium">INVOICE#</td>
                  {/* Using the clean INV-XXXXXX format instead of Mongo ID */}
                  <td>: {receipt.invoice_no || receipt._id}</td>
                </tr>
                <tr><td className="pr-4 text-gray-500 font-medium">DATE</td><td>: {invoiceDate}</td></tr>
                <tr><td className="pr-4 text-gray-500 font-medium">TERMS</td><td>: Due On Receipt</td></tr>
                <tr><td className="pr-4 text-gray-500 font-medium">DUE DATE</td><td>: {invoiceDate}</td></tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Addresses Section */}
        <div className="grid grid-cols-2 gap-8 mb-8">
          <div>
            <h3 className="text-xs font-bold text-gray-500 uppercase border-b border-gray-200 pb-1 mb-2">Bill To</h3>
            <p className="font-semibold">{customer?.name}</p>
            <p className="text-gray-600 whitespace-pre-wrap mt-1">
              {customer?.address !== 'N/A' ? customer?.address : ''}
            </p>
            <p className="text-gray-600 mt-1">Customer Email: {customer?.email}</p>
            {customer?.phone !== 'N/A' && <p className="text-gray-600 mt-1">Phone: {customer?.phone}</p>}
          </div>
        </div>

        {/* Line Items Table */}
        <table className="w-full mb-8 text-sm border-collapse">
          <thead>
            <tr className="bg-gray-50 border-y border-gray-300">
              <th className="py-2 px-3 text-left font-semibold text-gray-700 w-1/2">Item & Description</th>
              <th className="py-2 px-3 text-right font-semibold text-gray-700">Qty</th>
              <th className="py-2 px-3 text-right font-semibold text-gray-700">Rate</th>
              <th className="py-2 px-3 text-right font-semibold text-gray-700">Tax %</th>
              <th className="py-2 px-3 text-right font-semibold text-gray-700">Amount</th>
            </tr>
          </thead>
          <tbody>
            {lineItems?.map((item, idx) => (
              <tr key={idx} className="border-b border-gray-200 align-top">
                <td className="py-3 px-3 whitespace-pre-wrap">{item.description}</td>
                <td className="py-3 px-3 text-right">{item.qty.toFixed(3)}</td>
                <td className="py-3 px-3 text-right">{item.rate.toFixed(3)}</td>
                <td className="py-3 px-3 text-right">{item.taxPercent}%</td>
                <td className="py-3 px-3 text-right">{item.total.toFixed(3)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Totals Section */}
        <div className="flex justify-between items-start">
          <div className="w-1/2 pr-8 text-sm text-gray-600">
            <h4 className="font-semibold text-gray-800 mb-1">Total In Words</h4>
            <p className="italic mb-4 text-blue-800 font-medium">{summary?.totalInWords}</p>
            
            <h4 className="font-semibold text-gray-800 mb-1">Notes</h4>
            <p className="whitespace-pre-wrap">{summary?.paymentNote}</p>
          </div>
          
          <div className="w-1/2 max-w-sm">
            <table className="w-full text-sm">
              <tbody>
                <tr>
                  <td className="py-1 px-3 text-right text-gray-600">Sub Total</td>
                  <td className="py-1 px-3 text-right font-medium">₹{summary?.subTotal?.toFixed(3)}</td>
                </tr>
                {summary?.taxAmount > 0 && (
                  <tr>
                    <td className="py-1 px-3 text-right text-gray-600">Tax Amount</td>
                    <td className="py-1 px-3 text-right font-medium">₹{summary?.taxAmount?.toFixed(3)}</td>
                  </tr>
                )}
                <tr className="border-y border-gray-300 font-semibold text-base bg-blue-50/30">
                  <td className="py-2 px-3 text-right text-gray-900">Total</td>
                  <td className="py-2 px-3 text-right text-gray-900">₹{summary?.total?.toFixed(3)}</td>
                </tr>
                <tr>
                  <td className="py-2 px-3 text-right text-gray-600">Payment Made</td>
                  <td className="py-2 px-3 text-right text-green-600">(-) ₹{summary?.total?.toFixed(3)}</td>
                </tr>
                <tr className="bg-gray-50 border-t border-gray-300 font-bold">
                  <td className="py-2 px-3 text-right">Balance Due</td>
                  <td className="py-2 px-3 text-right">₹0.00</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Footer / Signature */}
        <div className="mt-16 flex justify-between items-end">
          <div className="text-xs text-gray-400 max-w-xs">
            *This is a computer-generated invoice and does not require a physical signature.
          </div>
         
        </div>

      </div>
    </div>
  );
}